import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import { FileText, LogOut, Trash2, ExternalLink, Clock, CheckCircle2, AlertCircle, BarChart3, Info, Shield, Settings, Link2, Mail, RefreshCw, Edit, Copy, Download, CheckCheck, Send, Users, Upload, QrCode, FileDown, Archive, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Admin() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settings, setSettings] = useState(null);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [showBulkActionModal, setShowBulkActionModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'archived'

  useEffect(() => {
    const savedCreds = localStorage.getItem('admin_credentials');
    if (savedCreds) {
      const creds = JSON.parse(savedCreds);
      setCredentials(creds);
      setIsAuthenticated(true);
      loadDashboardData(creds);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const auth = btoa(`${username}:${password}`);
      const creds = { username, auth };
      
      await axios.post(`${API}/admin/login`, {}, {
        headers: { 'Authorization': `Basic ${auth}` }
      });

      localStorage.setItem('admin_credentials', JSON.stringify(creds));
      setCredentials(creds);
      setIsAuthenticated(true);
      toast.success('Erfolgreich angemeldet!');
      loadDashboardData(creds);
    } catch (error) {
      toast.error('Ungültige Anmeldedaten');
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardData = async (creds) => {
    try {
      const [docsRes, statsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/documents`, {
          headers: { 'Authorization': `Basic ${creds.auth}` }
        }),
        axios.get(`${API}/admin/stats`, {
          headers: { 'Authorization': `Basic ${creds.auth}` }
        }),
        axios.get(`${API}/admin/settings`, {
          headers: { 'Authorization': `Basic ${creds.auth}` }
        })
      ]);
      
      setDocuments(docsRes.data);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Möchten Sie dieses Dokument wirklich löschen?')) return;

    try {
      await axios.delete(`${API}/admin/document/${documentId}`, {
        headers: { 'Authorization': `Basic ${credentials.auth}` }
      });
      toast.success('Dokument gelöscht');
      loadDashboardData(credentials);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Dokument ist für dauerhafte Aufbewahrung markiert');
      } else {
        toast.error('Fehler beim Löschen');
      }
    }
  };

  const handleShowDetails = async (documentId) => {
    try {
      const response = await axios.get(`${API}/admin/document/${documentId}/details`, {
        headers: { 'Authorization': `Basic ${credentials.auth}` }
      });
      setSelectedDocument(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      toast.error('Fehler beim Laden der Details');
    }
  };

  const toggleRetention = async (documentId, currentValue) => {
    try {
      await axios.put(
        `${API}/admin/document/${documentId}/retention`,
        { never_delete: !currentValue },
        { headers: { 'Authorization': `Basic ${credentials.auth}` } }
      );
      toast.success(!currentValue ? 'Dauerhaft gespeichert' : 'Speicherschutz entfernt');
      loadDashboardData(credentials);
      if (selectedDocument?.id === documentId) {
        setSelectedDocument({...selectedDocument, never_delete: !currentValue});
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const toggleBeschlussprotokoll = async (documentId, currentValue) => {
    try {
      await axios.put(
        `${API}/admin/document/${documentId}/beschlussprotokoll`,
        { is_beschlussprotokoll: !currentValue },
        { headers: { 'Authorization': `Basic ${credentials.auth}` } }
      );
      toast.success(!currentValue ? 'Beschlussprotokoll aktiviert' : 'Beschlussprotokoll deaktiviert');
      loadDashboardData(credentials);
      if (selectedDocument?.id === documentId) {
        setSelectedDocument({...selectedDocument, is_beschlussprotokoll: !currentValue});
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const toggleVerificationQR = async (documentId, currentValue) => {
    try {
      await axios.put(
        `${API}/admin/document/${documentId}/verification-qr`,
        { enable_verification_qr: !currentValue },
        { headers: { 'Authorization': `Basic ${credentials.auth}` } }
      );
      toast.success(!currentValue ? 'QR-Verifikation aktiviert' : 'QR-Verifikation deaktiviert');
      loadDashboardData(credentials);
      if (selectedDocument?.id === documentId) {
        setSelectedDocument({...selectedDocument, enable_verification_qr: !currentValue});
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const downloadAuditTrail = async (documentId) => {
    try {
      const response = await axios.get(
        `${API}/admin/document/${documentId}/audit-trail`,
        { 
          headers: { 'Authorization': `Basic ${credentials.auth}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Audit-Trail_${documentId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Audit-Trail heruntergeladen');
    } catch (error) {
      toast.error('Fehler beim Herunterladen des Audit-Trails');
    }
  };

  const downloadCurrentPdf = async (documentId) => {
    try {
      const response = await axios.get(
        `${API}/document/${documentId}/download-current`,
        { 
          headers: { 'Authorization': `Basic ${credentials.auth}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'dokument_aktuell.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF heruntergeladen');
    } catch (error) {
      toast.error('Fehler beim Herunterladen');
    }
  };

  const restoreDocument = async (documentId, days = 14) => {
    try {
      await axios.put(
        `${API}/admin/document/${documentId}/restore`,
        { days },
        { headers: { 'Authorization': `Basic ${credentials.auth}` } }
      );
      toast.success(`Dokument für ${days} Tage wiederhergestellt`);
      loadDashboardData(credentials);
      setShowDetailsModal(false);
    } catch (error) {
      toast.error('Fehler beim Wiederherstellen');
    }
  };

  const handlePdfUpload = async (documentId, file) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Bitte wählen Sie eine PDF-Datei aus');
      return;
    }

    setUploadingPdf(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/admin/document/${documentId}/upload-pdf`,
        formData,
        { 
          headers: { 
            'Authorization': `Basic ${credentials.auth}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      toast.success(response.data.message || 'PDF erfolgreich hochgeladen');
      loadDashboardData(credentials);
      if (selectedDocument?.id === documentId) {
        setSelectedDocument({...selectedDocument, original_filename: file.name});
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploadingPdf(false);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      await axios.put(
        `${API}/admin/settings`,
        newSettings,
        { headers: { 'Authorization': `Basic ${credentials.auth}` } }
      );
      setSettings(newSettings);
      toast.success('Einstellungen gespeichert');
      setShowSettingsModal(false);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const toggleSelectAll = () => {
    if (selectedDocIds.length === documents.length) {
      setSelectedDocIds([]);
    } else {
      setSelectedDocIds(documents.map(doc => doc.id));
    }
  };

  const toggleSelectDoc = (docId) => {
    if (selectedDocIds.includes(docId)) {
      setSelectedDocIds(selectedDocIds.filter(id => id !== docId));
    } else {
      setSelectedDocIds([...selectedDocIds, docId]);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedDocIds.length === 0) {
      toast.error('Bitte wählen Sie mindestens ein Dokument aus');
      return;
    }

    setBulkAction(action);
    setShowBulkActionModal(true);
  };

  const executeBulkAction = async () => {
    try {
      if (bulkAction === 'delete') {
        for (const docId of selectedDocIds) {
          await axios.delete(`${API}/admin/document/${docId}`, {
            headers: { 'Authorization': `Basic ${credentials.auth}` }
          });
        }
        toast.success(`${selectedDocIds.length} Dokument(e) gelöscht`);
      } else if (bulkAction === 'extend') {
        await axios.post(`${API}/admin/documents/extend-validity`, 
          { document_ids: selectedDocIds, days: 30 },
          { headers: { 'Authorization': `Basic ${credentials.auth}` } }
        );
        toast.success('Gültigkeit verlängert');
      } else if (bulkAction === 'resend') {
        await axios.post(`${API}/admin/documents/resend-emails`,
          { document_ids: selectedDocIds },
          { headers: { 'Authorization': `Basic ${credentials.auth}` } }
        );
        toast.success('E-Mails erneut versendet');
      }
      
      setShowBulkActionModal(false);
      setSelectedDocIds([]);
      loadDashboardData(credentials);
    } catch (error) {
      toast.error('Fehler bei der Aktion');
    }
  };

  const getDocumentLink = (docId) => {
    const link = `${window.location.origin}/sign/${docId}`;
    navigator.clipboard.writeText(link);
    toast.success('Link in Zwischenablage kopiert!');
  };

  const resendEmail = async (docId) => {
    try {
      await axios.post(`${API}/admin/document/${docId}/resend`,
        {},
        { headers: { 'Authorization': `Basic ${credentials.auth}` } }
      );
      toast.success('E-Mail erneut versendet');
    } catch (error) {
      toast.error('Fehler beim Versenden');
    }
  };

  const extendValidity = async (docId, days) => {
    try {
      await axios.put(`${API}/admin/document/${docId}/extend`,
        { days },
        { headers: { 'Authorization': `Basic ${credentials.auth}` } }
      );
      toast.success(`Gültigkeit um ${days} Tage verlängert`);
      loadDashboardData(credentials);
    } catch (error) {
      toast.error('Fehler beim Verlängern');
    }
  };

  const completeDocument = async (docId) => {
    if (!window.confirm('Möchten Sie dieses Dokument wirklich abschließen? Auch nicht unterschriebene Felder werden als abgeschlossen markiert.')) {
      return;
    }
    try {
      await axios.put(`${API}/admin/document/${docId}/complete`,
        {},
        { headers: { 'Authorization': `Basic ${credentials.auth}` } }
      );
      toast.success('Dokument wurde abgeschlossen');
      loadDashboardData(credentials);
      if (selectedDocument?.id === docId) {
        handleShowDetails(docId);
      }
    } catch (error) {
      toast.error('Fehler beim Abschließen des Dokuments');
    }
  };

  const resendCompletedDocument = async (docId) => {
    try {
      const response = await axios.post(`${API}/admin/document/${docId}/resend-completed`,
        {},
        { headers: { 'Authorization': `Basic ${credentials.auth}` } }
      );
      toast.success(response.data.message || 'Dokument wurde erneut gesendet');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Dokument ist noch nicht abgeschlossen');
      } else {
        toast.error('Fehler beim Senden des Dokuments');
      }
    }
  };

  const handleEditDocument = (doc) => {
    setEditingDocument(doc);
    setShowEditModal(true);
  };

  const saveDocumentEdit = async () => {
    try {
      await axios.put(`${API}/admin/document/${editingDocument.id}/update`,
        {
          recipient_emails: editingDocument.recipient_emails,
          expires_at: editingDocument.expires_at
        },
        { headers: { 'Authorization': `Basic ${credentials.auth}` } }
      );
      toast.success('Dokument aktualisiert');
      setShowEditModal(false);
      loadDashboardData(credentials);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_credentials');
    setIsAuthenticated(false);
    setCredentials(null);
    setDocuments([]);
    setStats(null);
    toast.success('Abgemeldet');
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\./g, '-');
  };

  const downloadDetailsPdf = () => {
    if (!selectedDocument) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('SignFlow - Dokument-Details', pageWidth / 2, y, { align: 'center' });
    y += 15;

    // Horizontal line
    doc.setLineWidth(0.5);
    doc.line(20, y, pageWidth - 20, y);
    y += 15;

    // General Information Section
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Allgemeine Informationen', 20, y);
    y += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    const addLabelValue = (label, value, indent = 20) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}:`, indent, y);
      doc.setFont('helvetica', 'normal');
      const textWidth = doc.getTextWidth(`${label}: `);
      doc.text(value || 'N/A', indent + textWidth, y);
      y += 7;
    };

    addLabelValue('Dateiname', selectedDocument.original_filename);
    addLabelValue('Dokument-ID', selectedDocument.id);
    addLabelValue('Status', selectedDocument.status === 'completed' ? 'Abgeschlossen' : 'Ausstehend');
    addLabelValue('Erstellt', formatDate(selectedDocument.created_at));
    addLabelValue('Upload IP', selectedDocument.upload_ip);
    addLabelValue('Beschlussprotokoll', selectedDocument.is_beschlussprotokoll ? 'Ja' : 'Nein');
    addLabelValue('Dauerhaft gespeichert', selectedDocument.never_delete ? 'Ja' : 'Nein');
    y += 5;

    // Recipient Details Section
    if (selectedDocument.recipient_details && selectedDocument.recipient_details.length > 0) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Empfänger-Details', 20, y);
      y += 10;

      selectedDocument.recipient_details.forEach((recipient, idx) => {
        if (y > 250) {
          doc.addPage();
          y = 20;
        }

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(`Empfänger ${idx + 1}: ${recipient.email}`, 20, y);
        y += 6;

        doc.setFont('helvetica', 'normal');
        if (recipient.name) {
          doc.text(`Name: ${recipient.name}`, 25, y);
          y += 5;
        }
        if (recipient.role) {
          doc.text(`Rolle: ${recipient.role}`, 25, y);
          y += 5;
        }
        y += 3;
      });
      y += 5;
    }

    // Signature Fields Section
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Unterschriftsfelder', 20, y);
    y += 10;

    selectedDocument.signature_fields.forEach((field, idx) => {
      // Check if we need a new page
      if (y > 250) {
        doc.addPage();
        y = 20;
      }

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(`Feld ${idx + 1}: ${field.text}`, 20, y);
      y += 6;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Status: ${field.signed ? 'Unterschrieben' : 'Ausstehend'}`, 25, y);
      y += 5;

      if (field.signed) {
        doc.text(`E-Mail: ${field.signer_email || 'N/A'}`, 25, y);
        y += 5;
        if (field.signer_name) {
          doc.text(`Name: ${field.signer_name}`, 25, y);
          y += 5;
        }
        if (field.signer_role) {
          doc.text(`Rolle: ${field.signer_role}`, 25, y);
          y += 5;
        }
        doc.text(`Unterschrieben am: ${formatDate(field.signed_at)}`, 25, y);
        y += 5;
        doc.text(`IP-Adresse: ${field.signer_ip || 'N/A'}`, 25, y);
        y += 5;
        doc.text(`Typ: ${field.signature_type === 'drawn' ? 'Gezeichnet' : 'Getippt'}`, 25, y);
        y += 5;
        if (field.signer_user_agent) {
          const userAgentLines = doc.splitTextToSize(`User Agent: ${field.signer_user_agent}`, pageWidth - 50);
          userAgentLines.forEach(line => {
            doc.text(line, 25, y);
            y += 5;
          });
        }
      }
      y += 5;
    });

    // Activity Log Section
    if (selectedDocument.activity_log && selectedDocument.activity_log.length > 0) {
      // Check if we need a new page
      if (y > 220) {
        doc.addPage();
        y = 20;
      }

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Aktivitäts-Protokoll', 20, y);
      y += 10;

      selectedDocument.activity_log.forEach((activity) => {
        // Check if we need a new page
        if (y > 260) {
          doc.addPage();
          y = 20;
        }

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        let actionText = activity.action;
        if (activity.action === 'document_uploaded') actionText = 'Dokument hochgeladen';
        if (activity.action === 'document_sent') actionText = 'Dokument versendet';
        if (activity.action === 'field_signed') actionText = 'Feld unterschrieben';
        if (activity.action === 'document_completed') actionText = 'Dokument abgeschlossen';
        doc.text(actionText, 20, y);
        y += 5;

        doc.setFont('helvetica', 'normal');
        doc.text(`Zeitpunkt: ${formatDate(activity.timestamp)}`, 25, y);
        y += 5;
        doc.text(`IP-Adresse: ${activity.ip_address || 'N/A'}`, 25, y);
        y += 5;
        if (activity.user_agent) {
          const uaLines = doc.splitTextToSize(`User Agent: ${activity.user_agent}`, pageWidth - 50);
          uaLines.forEach(line => {
            doc.text(line, 25, y);
            y += 5;
          });
        }
        y += 3;
      });
    }

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Erstellt am ${new Date().toLocaleDateString('de-DE')} | Seite ${i} von ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    // Generate filename: SignFlow_[PDF-Name]_[Datum].pdf
    const baseName = selectedDocument.original_filename.replace(/\.pdf$/i, '');
    const dateStr = formatDateShort(selectedDocument.created_at);
    const filename = `SignFlow_${baseName}_${dateStr}.pdf`;

    doc.save(filename);
    toast.success('PDF heruntergeladen');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full p-8">
          <div className="text-center mb-8">
            <div className="h-16 w-16 bg-zinc-950 rounded-md flex items-center justify-center mx-auto mb-4">
              <FileText className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Admin-Login
            </h2>
            <p className="text-zinc-600 mt-2">SignFlow Verwaltung</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="username">Benutzername</Label>
              <Input
                id="username"
                data-testid="admin-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-2"
                required
              />
            </div>

            <div>
              <Label htmlFor="password">Passwort</Label>
              <Input
                id="password"
                data-testid="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2"
                required
              />
            </div>

            <Button
              data-testid="admin-login-button"
              type="submit"
              disabled={loading}
              className="w-full bg-zinc-950 text-white hover:bg-zinc-800 h-12"
            >
              {loading ? 'Anmeldung...' : 'Anmelden'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-sm"
            >
              Zurück zur Startseite
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-zinc-950 rounded-md flex items-center justify-center">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Admin-Dashboard
                </h1>
                <p className="text-sm text-zinc-600">Angemeldet als {credentials?.username}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettingsModal(true)}
                className="gap-2"
              >
                <Settings className="h-4 w-4" />
                Einstellungen
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open('https://www.hvkurz.de', '_blank')}
              >
                zurück zur Verwaltung
              </Button>
              <Button
                data-testid="logout-button"
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="gap-2"
              >
                <LogOut className="h-4 w-4" />
                Abmelden
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 bg-zinc-100 rounded-full flex items-center justify-center">
                  <BarChart3 className="h-6 w-6 text-zinc-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.total_documents}</div>
                  <div className="text-sm text-zinc-600">Gesamt</div>
                </div>
              </div>
            </Card>

            <Card className="p-6 border-orange-200 bg-orange-50/30">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <Clock className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-orange-700">{stats.pending_documents}</div>
                  <div className="text-sm text-orange-600">Ausstehend</div>
                </div>
              </div>
            </Card>

            <Card className="p-6 border-green-200 bg-green-50/30">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-700">{stats.completed_documents}</div>
                  <div className="text-sm text-green-600">Abgeschlossen</div>
                </div>
              </div>
            </Card>

            <Card className="p-6 border-blue-200 bg-blue-50/30">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-700">{stats.today_documents}</div>
                  <div className="text-sm text-blue-600">Heute</div>
                </div>
              </div>
            </Card>

            <Card className="p-6 border-amber-200 bg-amber-50/30">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <Archive className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-amber-700">{stats.archived_documents || 0}</div>
                  <div className="text-sm text-amber-600">Archiviert</div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={activeTab === 'active' ? 'default' : 'outline'}
            onClick={() => setActiveTab('active')}
            className="gap-2"
          >
            <FileText className="h-4 w-4" />
            Aktive Dokumente
          </Button>
          <Button
            variant={activeTab === 'archived' ? 'default' : 'outline'}
            onClick={() => setActiveTab('archived')}
            className="gap-2"
          >
            <Archive className="h-4 w-4" />
            Archiv ({stats?.archived_documents || 0})
          </Button>
        </div>

        {/* Documents Table */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {activeTab === 'active' ? 'Aktive Dokumente' : 'Archivierte Dokumente'}
            </h2>
            {selectedDocIds.length > 0 && activeTab === 'active' && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-zinc-600">{selectedDocIds.length} ausgewählt</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkAction('resend')}
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Erneut senden
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkAction('extend')}
                  className="gap-2"
                >
                  <Clock className="h-4 w-4" />
                  Verlängern
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkAction('delete')}
                  className="gap-2 text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                  Löschen
                </Button>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="documents-table">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="text-left py-3 px-4 w-12">
                    <input
                      type="checkbox"
                      checked={selectedDocIds.length === documents.filter(d => activeTab === 'active' ? !d.is_archived : d.is_archived).length && documents.length > 0}
                      onChange={toggleSelectAll}
                      className="h-4 w-4"
                    />
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Dokument</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">ID</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Status</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">{activeTab === 'archived' ? 'Archiviert am' : 'Erstellt'}</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Empfänger</th>
                  <th className="text-right py-3 px-4 font-semibold text-sm">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {documents.filter(d => activeTab === 'active' ? !d.is_archived : d.is_archived).length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-12 text-zinc-500">
                      {activeTab === 'active' ? 'Noch keine aktiven Dokumente vorhanden' : 'Keine archivierten Dokumente vorhanden'}
                    </td>
                  </tr>
                ) : (
                  documents.filter(d => activeTab === 'active' ? !d.is_archived : d.is_archived).map((doc) => (
                    <tr key={doc.id} className={`border-b border-zinc-100 hover:bg-zinc-50 ${doc.is_archived ? 'bg-amber-50/30' : ''}`} data-testid={`doc-row-${doc.id}`}>
                      <td className="py-4 px-4">
                        <input
                          type="checkbox"
                          checked={selectedDocIds.includes(doc.id)}
                          onChange={() => toggleSelectDoc(doc.id)}
                          className="h-4 w-4"
                        />
                      </td>
                      <td className="py-4 px-4">
                        <div className="font-medium flex items-center gap-2">
                          {doc.original_filename}
                          {doc.is_archived && (
                            <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-100 px-2 py-0.5 rounded text-xs">
                              <Archive className="h-3 w-3" />
                              Archiviert
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <code className="text-xs bg-zinc-100 px-2 py-1 rounded">{doc.id.slice(0, 8)}</code>
                      </td>
                      <td className="py-4 px-4">
                        {doc.is_archived ? (
                          <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-100 px-2 py-1 rounded text-sm">
                            <Archive className="h-3 w-3" />
                            Archiviert
                          </span>
                        ) : doc.status === 'completed' ? (
                          <span className="inline-flex items-center gap-1 text-green-700 bg-green-100 px-2 py-1 rounded text-sm">
                            <CheckCircle2 className="h-3 w-3" />
                            Abgeschlossen
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-orange-700 bg-orange-100 px-2 py-1 rounded text-sm">
                            <Clock className="h-3 w-3" />
                            Ausstehend
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-sm text-zinc-600">
                        {formatDate(doc.created_at)}
                      </td>
                      <td className="py-4 px-4 text-sm text-zinc-600">
                        {doc.recipient_emails?.join(', ') || '-'}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            data-testid={`copy-link-${doc.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => getDocumentLink(doc.id)}
                            className="gap-1"
                            title="Link kopieren"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          {doc.is_archived ? (
                            <Button
                              data-testid={`restore-${doc.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => restoreDocument(doc.id, 14)}
                              className="gap-1 text-green-600 hover:text-green-700 hover:bg-green-50"
                              title="Wiederherstellen (14 Tage)"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          ) : (
                            <>
                              <Button
                                data-testid={`resend-${doc.id}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => resendEmail(doc.id)}
                                className="gap-1"
                                title="Erneut senden"
                              >
                                <Mail className="h-4 w-4" />
                              </Button>
                              <Button
                                data-testid={`extend-${doc.id}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => extendValidity(doc.id, 14)}
                                className="gap-1"
                                title="14 Tage verlängern"
                              >
                                <Clock className="h-4 w-4" />
                              </Button>
                              <Button
                                data-testid={`edit-${doc.id}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/admin/edit/${doc.id}`)}
                                className="gap-1"
                                title="Felder bearbeiten"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            data-testid={`download-current-${doc.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadCurrentPdf(doc.id)}
                            className="gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="PDF im aktuellen Zustand herunterladen"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            data-testid={`info-doc-${doc.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleShowDetails(doc.id)}
                            className="gap-1"
                            title="Details"
                          >
                            <Info className="h-4 w-4" />
                          </Button>
                          <Button
                            data-testid={`view-doc-${doc.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(`/sign/${doc.id}`, '_blank')}
                            className="gap-1"
                            title="Öffnen"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          <Button
                            data-testid={`delete-doc-${doc.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(doc.id)}
                            className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Löschen"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Info Card */}
        <Card className="p-6 mt-8 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-4">
            <AlertCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">E-Mail-Zusammenfassung</h3>
              <p className="text-blue-800 text-sm">
                {settings?.email_summary_frequency === 'disabled' && 'E-Mail-Zusammenfassung ist deaktiviert.'}
                {settings?.email_summary_frequency === 'daily' && `Sie erhalten täglich um 12:00 Uhr eine E-Mail-Zusammenfassung an ${credentials?.username}.`}
                {settings?.email_summary_frequency === 'monday_only' && `Sie erhalten jeden Montag um 12:00 Uhr eine E-Mail-Zusammenfassung an ${credentials?.username}.`}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Footer */}
      <footer className="border-t border-zinc-200 bg-white mt-12">
        <div className="container mx-auto px-6 py-8">
          <div className="text-center text-sm text-zinc-500 space-y-2">
            <p>Entwickelt von Scholze-Kurz & Kurz Immobilien GmbH</p>
            <div className="flex items-center justify-center gap-4">
              <a
                href="https://www.hvkurz.de/impressum"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-600 hover:text-zinc-900 transition-colors"
              >
                Impressum
              </a>
              <span>|</span>
              <a
                href="https://www.hvkurz.de/datenschutzerklaerung/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-600 hover:text-zinc-900 transition-colors"
              >
                Datenschutz
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Details Modal */}
      {showDetailsModal && selectedDocument && (
        <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="text-2xl" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Dokument-Details
                </DialogTitle>
                <div className="flex items-center gap-2 mr-8">
                  {selectedDocument.status === 'completed' && (
                    <Button
                      data-testid="resend-completed-button"
                      variant="outline"
                      size="sm"
                      onClick={() => resendCompletedDocument(selectedDocument.id)}
                      className="gap-2 border-blue-500 text-blue-700 hover:bg-blue-50"
                    >
                      <Send className="h-4 w-4" />
                      Erneut senden
                    </Button>
                  )}
                  {selectedDocument.status !== 'completed' && (
                    <Button
                      data-testid="complete-document-button"
                      variant="outline"
                      size="sm"
                      onClick={() => completeDocument(selectedDocument.id)}
                      className="gap-2 border-green-500 text-green-700 hover:bg-green-50"
                    >
                      <CheckCheck className="h-4 w-4" />
                      Abschließen
                    </Button>
                  )}
                  <Button
                    data-testid="download-details-pdf"
                    variant="outline"
                    size="sm"
                    onClick={downloadDetailsPdf}
                    className="gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Als PDF speichern
                  </Button>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-6">
              {/* Basic Info */}
              <Card className="p-4 bg-zinc-50">
                <h3 className="font-semibold mb-3">Allgemeine Informationen</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-zinc-600">Dateiname:</span>
                    <div className="font-medium">{selectedDocument.original_filename}</div>
                  </div>
                  <div>
                    <span className="text-zinc-600">Dokument-ID:</span>
                    <div className="font-mono text-xs">{selectedDocument.id}</div>
                  </div>
                  <div>
                    <span className="text-zinc-600">Status:</span>
                    <div className="font-medium">
                      {selectedDocument.status === 'completed' ? '✓ Abgeschlossen' : '⏳ Ausstehend'}
                    </div>
                  </div>
                  <div>
                    <span className="text-zinc-600">Erstellt:</span>
                    <div className="font-medium">{formatDate(selectedDocument.created_at)}</div>
                  </div>
                  <div>
                    <span className="text-zinc-600">PDF herunterladen:</span>
                    <div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadCurrentPdf(selectedDocument.id)}
                        className="gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                        data-testid="download-current-pdf-modal"
                      >
                        <Download className="h-4 w-4" />
                        Speichern & Download
                      </Button>
                    </div>
                  </div>
                  <div>
                    <span className="text-zinc-600">Beschlussprotokoll:</span>
                    <div>
                      <Button
                        size="sm"
                        variant={selectedDocument.is_beschlussprotokoll ? 'default' : 'outline'}
                        onClick={() => toggleBeschlussprotokoll(selectedDocument.id, selectedDocument.is_beschlussprotokoll)}
                        className="gap-2"
                      >
                        <Users className="h-4 w-4" />
                        {selectedDocument.is_beschlussprotokoll ? 'Aktiviert' : 'Deaktiviert'}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <span className="text-zinc-600">PDF ersetzen:</span>
                    <div>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept=".pdf"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handlePdfUpload(selectedDocument.id, file);
                            }
                            e.target.value = '';
                          }}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-2"
                          disabled={uploadingPdf}
                          asChild
                        >
                          <span>
                            <Upload className="h-4 w-4" />
                            {uploadingPdf ? 'Lädt...' : 'PDF hochladen'}
                          </span>
                        </Button>
                      </label>
                    </div>
                  </div>
                  <div>
                    <span className="text-zinc-600">Upload IP:</span>
                    <div className="font-mono text-xs">{selectedDocument.upload_ip || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-zinc-600">Dauerhaft gespeichert:</span>
                    <div>
                      <Button
                        size="sm"
                        variant={selectedDocument.never_delete ? 'default' : 'outline'}
                        onClick={() => toggleRetention(selectedDocument.id, selectedDocument.never_delete)}
                        className="gap-2"
                      >
                        <Shield className="h-4 w-4" />
                        {selectedDocument.never_delete ? 'Geschützt' : 'Nicht geschützt'}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <span className="text-zinc-600">QR-Verifikation:</span>
                    <div>
                      <Button
                        size="sm"
                        variant={selectedDocument.enable_verification_qr ? 'default' : 'outline'}
                        onClick={() => toggleVerificationQR(selectedDocument.id, selectedDocument.enable_verification_qr)}
                        className="gap-2"
                        data-testid="toggle-verification-qr"
                      >
                        <QrCode className="h-4 w-4" />
                        {selectedDocument.enable_verification_qr ? 'Aktiviert' : 'Deaktiviert'}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <span className="text-zinc-600">Audit-Trail:</span>
                    <div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadAuditTrail(selectedDocument.id)}
                        className="gap-2"
                        data-testid="download-audit-trail"
                      >
                        <FileDown className="h-4 w-4" />
                        Herunterladen
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Recipient Details */}
              {selectedDocument.recipient_details && selectedDocument.recipient_details.length > 0 && (
                <Card className="p-4">
                  <h3 className="font-semibold mb-3">Empfänger-Details</h3>
                  <div className="space-y-2">
                    {selectedDocument.recipient_details.map((recipient, idx) => (
                      <div key={idx} className="flex items-center justify-between border border-zinc-200 rounded p-3 text-sm">
                        <div className="space-y-1">
                          <div className="font-medium">{recipient.email}</div>
                          {recipient.name && (
                            <div className="text-zinc-600">Name: {recipient.name}</div>
                          )}
                          {recipient.role && (
                            <div className="text-zinc-500 text-xs">{recipient.role}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Signature Fields */}
              <Card className="p-4">
                <h3 className="font-semibold mb-3">Unterschriftsfelder</h3>
                <div className="space-y-3">
                  {selectedDocument.signature_fields.map((field, idx) => (
                    <div key={field.id} className="border border-zinc-200 rounded p-3 text-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">Feld {idx + 1}: {field.text}</span>
                        {field.signed ? (
                          <span className="text-green-600 flex items-center gap-1">
                            <CheckCircle2 className="h-4 w-4" />
                            Unterschrieben
                          </span>
                        ) : (
                          <span className="text-orange-600">Ausstehend</span>
                        )}
                      </div>
                      {field.signed && (
                        <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-zinc-600 bg-zinc-50 p-2 rounded">
                          <div>
                            <strong>E-Mail:</strong> {field.signer_email}
                          </div>
                          <div>
                            <strong>Unterschrieben am:</strong> {formatDate(field.signed_at)}
                          </div>
                          {field.signer_name && (
                            <div>
                              <strong>Name:</strong> {field.signer_name}
                            </div>
                          )}
                          {field.signer_role && (
                            <div>
                              <strong>Rolle:</strong> {field.signer_role}
                            </div>
                          )}
                          <div>
                            <strong>IP-Adresse:</strong> <code className="bg-white px-1">{field.signer_ip || 'N/A'}</code>
                          </div>
                          <div>
                            <strong>Typ:</strong> {field.signature_type === 'drawn' ? 'Gezeichnet' : 'Getippt'}
                          </div>
                          {field.signer_user_agent && (
                            <div className="col-span-2">
                              <strong>User Agent:</strong>
                              <div className="text-xs font-mono bg-white p-1 mt-1 rounded break-all">
                                {field.signer_user_agent}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>

              {/* Activity Log */}
              {selectedDocument.activity_log && selectedDocument.activity_log.length > 0 && (
                <Card className="p-4">
                  <h3 className="font-semibold mb-3">Aktivitäts-Protokoll</h3>
                  <div className="space-y-2">
                    {selectedDocument.activity_log.map((activity, idx) => (
                      <div key={idx} className="border-l-2 border-zinc-300 pl-4 py-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="font-medium text-sm">
                              {activity.action === 'document_uploaded' && '📤 Dokument hochgeladen'}
                              {activity.action === 'document_sent' && '✉️ Dokument versendet'}
                              {activity.action === 'field_signed' && '✍️ Feld unterschrieben'}
                              {activity.action === 'document_completed' && '✅ Dokument abgeschlossen'}
                            </div>
                            <div className="text-xs text-zinc-500 mt-1">
                              {formatDate(activity.timestamp)}
                            </div>
                          </div>
                          <div className="text-xs text-right">
                            <div className="font-mono bg-zinc-100 px-2 py-1 rounded">
                              {activity.ip_address}
                            </div>
                          </div>
                        </div>
                        {activity.details && (
                          <div className="text-xs text-zinc-600 mt-2 bg-zinc-50 p-2 rounded">
                            {JSON.stringify(activity.details, null, 2)}
                          </div>
                        )}
                        {activity.user_agent && (
                          <div className="text-xs text-zinc-500 mt-1 font-mono break-all">
                            {activity.user_agent}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Settings Modal */}
      {showSettingsModal && settings && (
        <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                E-Mail-Einstellungen
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>E-Mail-Zusammenfassung</Label>
                <Select
                  value={settings.email_summary_frequency}
                  onValueChange={(value) => setSettings({...settings, email_summary_frequency: value})}
                >
                  <SelectTrigger className="w-full mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Täglich um 12:00 Uhr</SelectItem>
                    <SelectItem value="monday_only">Nur Montags um 12:00 Uhr</SelectItem>
                    <SelectItem value="disabled">Deaktiviert</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-500 mt-2">
                  {settings.email_summary_frequency === 'daily' && 'Sie erhalten jeden Tag um 12:00 Uhr eine Zusammenfassung, wenn es neue Dokumente gibt.'}
                  {settings.email_summary_frequency === 'monday_only' && 'Sie erhalten jeden Montag um 12:00 Uhr eine Zusammenfassung der letzten Woche, wenn es neue Dokumente gibt.'}
                  {settings.email_summary_frequency === 'disabled' && 'Sie erhalten keine automatischen E-Mail-Zusammenfassungen.'}
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
                <strong>Hinweis:</strong> E-Mails werden nur versendet, wenn es neue Dokumente gibt.
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowSettingsModal(false)}
                  className="flex-1"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={() => updateSettings(settings)}
                  className="flex-1 bg-zinc-950 text-white hover:bg-zinc-800"
                >
                  Speichern
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Bulk Action Modal */}
      {showBulkActionModal && (
        <Dialog open={showBulkActionModal} onOpenChange={setShowBulkActionModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {bulkAction === 'delete' && 'Dokumente löschen'}
                {bulkAction === 'extend' && 'Gültigkeit verlängern'}
                {bulkAction === 'resend' && 'E-Mails erneut senden'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p>
                {bulkAction === 'delete' && `Möchten Sie ${selectedDocIds.length} Dokument(e) wirklich löschen?`}
                {bulkAction === 'extend' && `Gültigkeit von ${selectedDocIds.length} Dokument(en) um 30 Tage verlängern?`}
                {bulkAction === 'resend' && `E-Mails für ${selectedDocIds.length} Dokument(e) erneut versenden?`}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowBulkActionModal(false)}
                  className="flex-1"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={executeBulkAction}
                  className="flex-1 bg-zinc-950 text-white hover:bg-zinc-800"
                >
                  Bestätigen
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Document Modal */}
      {showEditModal && editingDocument && (
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Dokument bearbeiten</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Empfänger-E-Mails</Label>
                <Input
                  value={editingDocument.recipient_emails?.join(', ') || ''}
                  onChange={(e) => setEditingDocument({
                    ...editingDocument,
                    recipient_emails: e.target.value.split(',').map(s => s.trim())
                  })}
                  className="mt-2"
                  placeholder="email1@example.com, email2@example.com"
                />
              </div>
              <div>
                <Label>Gültig bis</Label>
                <Input
                  type="datetime-local"
                  value={editingDocument.expires_at ? new Date(editingDocument.expires_at).toISOString().slice(0, 16) : ''}
                  onChange={(e) => setEditingDocument({
                    ...editingDocument,
                    expires_at: new Date(e.target.value).toISOString()
                  })}
                  className="mt-2"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={saveDocumentEdit}
                  className="flex-1 bg-zinc-950 text-white hover:bg-zinc-800"
                >
                  Speichern
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}